import { createTheme, Theme } from "@mui/material/styles";
import { lightTheme, darkTheme } from "./custom";
import { typo } from "./typography";
import * as locales from "@mui/material/locale";

const OpenSans =
  process.env.REACT_APP_ENV === "development"
    ? "http://localhost:5600/fonts/open-sans-regular.ttf"
    : "https://portal.ultramanpowercompany.com/fonts/open-sans-regular.ttf";

const overrides = {
  typography: { ...typo },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Open Sans';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: local('Open Sans'), local('OpenSans-Regular'), url(${OpenSans}) format('ttf');
          unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.32); }
      `,
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none" as const,
          fontWeight: 600,
          letterSpacing: "0.01em",
          transition: "all 0.18s ease",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
            boxShadow: "none",
          },
        },
        outlined: {
          fontWeight: 600,
        },
        text: {
          fontWeight: 600,
        },
        sizeLarge: {
          padding: "10px 28px",
          fontSize: "1rem",
        },
        sizeSmall: {
          padding: "4px 14px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: "0 6px 24px rgba(0,0,0,0.11)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        },
        elevation2: {
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        },
        elevation4: {
          boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
        },
        elevation8: {
          boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "8px !important",
          overflow: "hidden",
          marginBottom: 8,
          "&:before": { display: "none" },
          "&.Mui-expanded": { marginBottom: 8 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
          "&.Mui-expanded": { minHeight: 48 },
        },
        content: {
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          "&.Mui-expanded": { margin: "8px 0" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 6,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: "1.15rem",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: "0.78rem",
          fontWeight: 500,
          padding: "5px 10px",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 700,
            fontSize: "0.8rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase" as const,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color 0.12s ease",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(224,224,224,0.6)",
          padding: "10px 16px",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            transition: "box-shadow 0.15s ease",
            "&.Mui-focused": {
              boxShadow: "0 0 0 3px rgba(13,99,125,0.12)",
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 3,
          borderRadius: "3px 3px 0 0",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontWeight: 600,
          transition: "color 0.15s ease",
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          fontSize: "0.7rem",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          "body[dir='rtl'] &": {
            transform: "scaleX(-1)",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "body[dir='rtl'] &": {
            right: 0,
            transformOrigin: "top right",
            transform: "translate(-14px, 9px) scale(0.75)",
          },
        },
        shrink: {
          "body[dir='rtl'] &": {
            right: 0,
            transformOrigin: "top right",
            transform: "translate(-14px, -9px) scale(0.75)",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          "body[dir='rtl'] &": {
            textAlign: "right",
          },
        },
      },
    },
  },
  customShadows: {
    widget:
      "0px 3px 11px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
    widgetDark:
      "0px 3px 18px 0px #4558A3B3, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
    widgetWide:
      "0px 12px 33px 0px #E8EAFC, 0 3px 3px -2px #B2B2B21A, 0 1px 8px 0 #9A9A9A1A",
  },
};

const lang = localStorage.getItem("i18nextLng")
  ? localStorage.getItem("i18nextLng")
  : "en";
const locale = lang === "ar" ? "arSA" : lang === "et" ? "amET" : "enUS";
const themes = {
  overrides,
  light: createTheme({ ...lightTheme, ...overrides }, locales[locale]) as Theme,
  dark: createTheme({ ...darkTheme, ...overrides }, locales[locale]) as Theme,
};

export default themes;
